import { useCallback, useEffect, useState } from "react";
import { useViewerActions, useViewerStore } from "../stores/viewerStore";
import {
  DEFAULT_SITE_VISIBILITY_UI_STATE,
  getSiteVisibilityUiState,
  setSiteVisibilityUiState,
  type SiteVisibilityUiState,
} from "./visibilityEvents";

export type SiteVisibilityState = SiteVisibilityUiState;

const defaultState = (): SiteVisibilityState => ({
  ...DEFAULT_SITE_VISIBILITY_UI_STATE,
});

let rememberedSiteVisibilityState: SiteVisibilityState = defaultState();

export type UseSiteVisibilityControlsArgs = {
  siteUuid: string | null;
  enabled?: boolean;
  initialState?: Partial<SiteVisibilityState>;
  onStateChange?: (state: SiteVisibilityState) => void;
};

export type UseSiteVisibilityControlsResult = {
  state: SiteVisibilityState;
  disabled: boolean;
  focusDisabled: boolean;
  toggleFocus: () => void;
  toggleOrientationAxes: () => void;
  reset: () => void;
};

export function useSiteVisibilityControls({
  siteUuid,
  enabled = true,
  initialState,
  onStateChange,
}: UseSiteVisibilityControlsArgs): UseSiteVisibilityControlsResult {
  const showSites = useViewerStore((s) => s.showSites);
  const viewerActions = useViewerActions();
  const [state, setStateInternal] = useState<SiteVisibilityState>(() => ({
    ...defaultState(),
    ...rememberedSiteVisibilityState,
    labelsOff: getSiteVisibilityUiState().labelsOff,
    ...initialState,
  }));

  const disabled = !enabled || !siteUuid;
  const focusDisabled = disabled;

  const setState = useCallback(
    (patch: Partial<SiteVisibilityState>) => {
      setStateInternal((prev) => {
        const next = { ...prev, ...patch };
        rememberedSiteVisibilityState = next;
        onStateChange?.(next);
        return next;
      });
    },
    [onStateChange]
  );

  const ensureSitesVisible = useCallback(() => {
    if (!showSites) {
      viewerActions.toggleShowSites();
    }
  }, [showSites, viewerActions]);

  useEffect(() => {
    setSiteVisibilityUiState({
      focus: state.focus,
      orientationAxes: state.orientationAxes,
    });
  }, [state.focus, state.orientationAxes]);

  useEffect(() => {
    return () => {
      setSiteVisibilityUiState({
        focus: false,
        orientationAxes: false,
      });
    };
  }, []);

  const toggleFocus = useCallback(() => {
    if (focusDisabled) return;
    const next = !state.focus;
    if (next) ensureSitesVisible();
    setState({ focus: next });
  }, [focusDisabled, ensureSitesVisible, setState, state.focus]);

  const toggleOrientationAxes = useCallback(() => {
    if (disabled) return;
    const next = !state.orientationAxes;
    if (next) ensureSitesVisible();
    setState({ orientationAxes: next });
  }, [disabled, ensureSitesVisible, setState, state.orientationAxes]);

  const reset = useCallback(() => {
    setSiteVisibilityUiState(DEFAULT_SITE_VISIBILITY_UI_STATE);
    setStateInternal(() => {
      const next = { ...defaultState(), ...initialState };
      rememberedSiteVisibilityState = next;
      onStateChange?.(next);
      return next;
    });
  }, [initialState, onStateChange]);

  return {
    state,
    disabled,
    focusDisabled,
    toggleFocus,
    toggleOrientationAxes,
    reset,
  };
}
